import { Box, Typography, useTheme, useMediaQuery, CircularProgress } from '@mui/material';
import Carousel from 'react-material-ui-carousel';
import { NoticiaList } from '../../../../typesSite/noticia/Noticia';
import { gerarUrlCapa } from '../noticia/create/gerarUrlCapa';
import ImageIcon from '@mui/icons-material/Image';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import configSiteUrls from '../../configs/configSiteUrls';

type Props = {
  noticiaAnexos: NoticiaList[] | null;
  isFetching?: boolean;
};

export function NoticiaViewCarrossel({ noticiaAnexos, isFetching }: Props) {
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down('sm'));
  const isMd = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  const [currentIndex, setCurrentIndex] = useState(0);
  const navigate = useNavigate();

  if (isFetching) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2
        }}
      >
        <CircularProgress />
        <Typography variant="body2" color="textSecondary">Carregando notícias em destaque...</Typography>
      </Box>
    );
  }

  if (!noticiaAnexos || noticiaAnexos.length === 0) {
    return (
      <Typography variant="body2" color="textSecondary">
        Nenhuma notícia principal encontrada.
      </Typography>
    );
  }

  const handleImageError = (event: any) => {
    event.target.src = ''; // fallback
  };

  const getImageHeight = () => {
    if (isSm) return '300px';
    if (isMd) return '400px';
    return '500px';
  };

  const handleClick = (id: string) => {
    navigate(`${configSiteUrls.SITE_NOTICIA_PUBLIC_VIEW_LK}${id}`);
  };

  return (
    <Box mt={0}>
      <Carousel
        swipe
        autoPlay
        animation="fade"
        navButtonsAlwaysVisible
        index={currentIndex}
        onChange={(now?: number, previous?: number) => {
          if (now !== undefined) setCurrentIndex(now);
        }}
      >
        {noticiaAnexos.map((item, index) => {
          const capaUrl = gerarUrlCapa(item.imagem ?? '', 'NOTICIA');

          return (
            <Box
              key={index}
              display="flex"
              flexDirection="column"
              alignItems="center"
              sx={{
                width: '100%',
                borderRadius: 2,
                overflow: 'hidden',
                position: 'relative'
              }}
              onClick={() => handleClick(item.id)}
            >
              {/* Container da imagem */}
              <Box
                sx={{
                  width: '100%',
                  height: getImageHeight(),
                  overflow: 'hidden',
                  borderRadius: 2,
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {capaUrl ? (
                  <Box
                    component="img"
                    src={capaUrl}
                    alt={item.titulo}
                    onError={handleImageError}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transition: 'transform 0.5s ease',
                      '&:hover': {
                        transform: 'scale(1.05)' // leve zoom ao passar o mouse
                      }
                    }}
                  />
                ) : (
                  <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" sx={{ color: '#999', height: '100%' }}>
                    <ImageIcon sx={{ fontSize: 60 }} />
                    <Typography variant="body2">Imagem não disponível</Typography>
                  </Box>
                )}

                {/* Título centralizado com fade-in/out */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: '90%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    backgroundColor: 'rgba(0,0,0,0.6)',
                    borderRadius: 1,
                    px: 2,
                    py: 1,
                    width: '90%', // largura máxima do título
                    textAlign: 'center', // centraliza o texto
                    opacity: currentIndex === index ? 1 : 0,
                    transition: 'opacity 1s ease-in-out'
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: { xs: '1.2rem', sm: '1.5rem', md: '1.8rem' }, // tamanho responsivo
                      fontWeight: 500, // médio, não tão grosso
                      color: '#fff',
                      lineHeight: 1.2,
                      wordBreak: 'break-word',
                      textAlign: 'center',
                      textShadow: '1px 1px 3px rgba(0,0,0,0.7)'
                    }}
                  >
                    {item.titulo}
                  </Typography>
                </Box>
              </Box>
            </Box>
          );
        })}
      </Carousel>
    </Box>
  );
}
